import { useEffect, useState } from 'react'

/**
 * Debounced mirror of `value`, settling `delayMs` after the last change.
 *
 * `onSettle` runs in the same tick as the update, so companion state (a page index, say) batches
 * into one render instead of firing a request against the old value first.
 */
export function useDebouncedValue<T>(value: T, delayMs: number, onSettle?: () => void): T {
  const [settled, setSettled] = useState(value)

  useEffect(() => {
    if (value === settled) return
    const timeoutId = setTimeout(() => {
      setSettled(value)
      onSettle?.()
    }, delayMs)
    return () => clearTimeout(timeoutId)
    // `onSettle` is intentionally excluded: callers pass an inline closure, and including it
    // would re-arm the timer on every render and never settle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, settled, delayMs])

  return settled
}
